import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useGameStore } from '@/stores/game';
import { extractErrorMessage } from '@/utils/errorHandler';
import { getClarityHeaders } from '@/utils/clarity';
import { isActiveLiteIntegration } from '@/utils/integrationMode';
import { readChannelIdFromUrl } from '@/utils/channel';
import { getCurrentLocale } from '@/i18n';

const apiBaseUrl = process.env.VUE_APP_API_BASE_URL as string;
const api: AxiosInstance = axios.create({
  baseURL: apiBaseUrl,
  timeout: 30000,
});

/**
 * Dynamically update the API base URL.
 * Essential for iframe integrations where the parent provides the endpoint.
 */
export function setApiBaseUrl(url: string): void {
  if (!url) return;
  
  let formattedUrl = url.trim();
  if (formattedUrl.endsWith('/')) {
    formattedUrl = formattedUrl.slice(0, -1);
  }

  // Ensure /api/v1 is present if it's a domain-only URL
  // but avoid double-prefixing if it's already there
  if (!formattedUrl.includes('/api/v1')) {
    formattedUrl = `${formattedUrl}/api/v1`;
  }
  
  api.defaults.baseURL = formattedUrl;
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('bitville_api_base_url', formattedUrl);
  }
  console.log(`[API] Base URL updated to: ${formattedUrl}. Interceptor will now prefix requests if necessary.`);
}

const AUTH_REQUIRED_ENDPOINTS = [
  '/wagers',
  '/wager-details',
  '/place-bet',
  '/booked-bets',
  '/stream-video',
  '/promotions/claim',
  '/promotions/active',
];

const ACCOUNT_CONTEXT_BOOKING_ENDPOINTS = ['/book-bet', '/load-booked-bet', '/bet-builder/match'];

/** Same auth headers as game-settings (account, currency, optional token/channel). */
const ACCOUNT_CONTEXT_SETTINGS_ENDPOINTS = [
  '/game-settings',
  '/game-details',
  // Draw list endpoints need account/channel so cutoff_override is applied server-side.
  '/upcoming-draws',
  '/products',
  // Account-scoped catalog/results so AccountGameExclusion filtering applies.
  '/results',
  '/games',
  '/catalog',
  '/next-draws-by-date',
  '/stream-video',
  '/promotions/claim',
  '/promotions/active',
];

/** 401 here: clear token locally only; do not fire session-invalid (parent reload drops /wagers). */
function isWagersHistory401Scope(url: string | undefined): boolean {
  if (!url) return false;
  const path = url.split('?')[0].replace(/\/$/, '') || '';
  return path.endsWith('/wagers') || path.includes('/wager-details');
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.params = { ...(config.params || {}) };

  // --- Nginx Unified Domain Handling ---
  // If config.url starts with / but is not /api/v1, and baseURL has /api/v1, 
  // Axios will normally discard the /api/v1. We must force it back.
  if (config.url && config.url.startsWith('/') && !config.url.startsWith('/api/v1')) {
    const currentBaseUrl = config.baseURL || api.defaults.baseURL || '';
    if (typeof currentBaseUrl === 'string' && currentBaseUrl.includes('/api/v1')) {
      try {
        // Construct absolute URL for checking
        let urlObj: URL;
        if (currentBaseUrl.startsWith('http')) {
          urlObj = new URL(currentBaseUrl);
        } else {
          // Relative baseURL handling
          const base = window.location.origin;
          const fullPath = currentBaseUrl.startsWith('/') ? currentBaseUrl : `/${currentBaseUrl}`;
          urlObj = new URL(fullPath, base);
        }

        if (urlObj.pathname.includes('/api/v1')) {
          const prefix = urlObj.pathname.endsWith('/') ? urlObj.pathname.slice(0, -1) : urlObj.pathname;
          // Prepend prefix to the url
          const urlWithoutSlash = config.url.startsWith('/') ? config.url.substring(1) : config.url;
          config.url = `${prefix}/${urlWithoutSlash}`;
          // Set baseURL to just the origin so axios doesn't strip the prefix in config.url
          config.baseURL = urlObj.origin;
        }
      } catch (e) {
        // Fallback for malformed URLs
        if (currentBaseUrl.endsWith('/api/v1')) {
          config.url = `/api/v1${config.url}`;
          config.baseURL = currentBaseUrl.replace(/\/api\/v1$/, '');
        }
      }
    }
  }

  // --- Standard Interceptors ---
  if (!isActiveLiteIntegration()) {
    const clarityHeaders = getClarityHeaders();
    Object.assign(config.headers, clarityHeaders);
  }

  const locale = getCurrentLocale();
  if (locale) {
    (config.headers as Record<string, string>)['lang'] = locale;
  }

  const authStore = useAuthStore();
  authStore.refreshAuthFromExternalSources();
  authStore.reinitializeFromSession();

  const readQueryParam = (name: string): string | null => {
    try {
      const v = new URLSearchParams(window.location.search).get(name);
      return v ? v.split('?')[0].trim() || null : null;
    } catch {
      return null;
    }
  };

  const resolveTenantAccount = (): string | null => authStore.resolveTenantAccount();

  const isAccountContextBooking = ACCOUNT_CONTEXT_BOOKING_ENDPOINTS.some((endpoint) =>
    config.url?.includes(endpoint)
  );

  if (isAccountContextBooking) {
    const gameStore = useGameStore();
    const account = resolveTenantAccount();
    if (account) {
      (config.headers as Record<string, string>)['auth-account'] = account;
    }
    const currency =
      gameStore.getBetSettings?.currency_code ||
      sessionStorage.getItem('auth_currency') ||
      readQueryParam('currency');
    if (currency) {
      (config.headers as Record<string, string>)['auth-currency'] = currency;
    }
    if (authStore.token) {
      (config.headers as Record<string, string>)['auth-token'] = authStore.token;
    }
    return config;
  }
  
  // Add auth-account and auth-currency headers for endpoints that need them
  const needsAccountContextHeaders = ACCOUNT_CONTEXT_SETTINGS_ENDPOINTS.some((endpoint) =>
    config.url?.includes(endpoint)
  );
  if (needsAccountContextHeaders) {
    const gameStore = useGameStore();
    const account = resolveTenantAccount();
    if (account) config.headers['auth-account'] = account;

    const currency =
      gameStore.getBetSettings?.currency_code ||
      sessionStorage.getItem('auth_currency') ||
      readQueryParam('currency');
    if (currency) config.headers['auth-currency'] = currency;
    if (authStore.token) config.headers['auth-token'] = authStore.token;
    const channelId = readChannelIdFromUrl();
    if (channelId) config.headers['auth-channel'] = String(channelId);
    // Fall through when auth is also required (e.g. /stream-video) so authenticate() can run.
    if (!AUTH_REQUIRED_ENDPOINTS.some((endpoint) => config.url?.includes(endpoint))) {
      return config;
    }
  }

  const requiresAuth = AUTH_REQUIRED_ENDPOINTS.some(endpoint => config.url?.includes(endpoint));

  if (requiresAuth) {
    if (!authStore.isAuthenticated) {
      return authStore.authenticate()
        .then(() => {
          if (authStore.isAuthenticated) Object.assign(config.headers, authStore.getAuthHeaders);
          return config;
        })
        .catch((error) => {
          console.warn('[API] Auth failed, proceeding without headers:', error);
          return config;
        });
    }
    Object.assign(config.headers, authStore.getAuthHeaders);
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const reqUrl = error.config?.url || '';
      const authStore = useAuthStore();
      if (isWagersHistory401Scope(reqUrl)) {
        authStore.clearInvalidTokenKeepAccount();
      } else {
        authStore.clearInvalidCredentials();
      }
    }

    const errorMessage = error?.response?.data?.message || error?.message || '';
    if (errorMessage.toLowerCase().includes('token expired or invalid')) {
      const authStore = useAuthStore();
      authStore.clearInvalidTokenKeepAccount();
    }

    error.userMessage = extractErrorMessage(error);
    return Promise.reject(error);
  }
);

export default api;
